import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { assessmentApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import {
  FileText,
  Calendar,
  TrendingUp,
  ChevronRight,
  Plus,
  Loader2
} from 'lucide-react';

export default function ReportsPage() {
  const { isAuthenticated, isGuest } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && !isGuest) {
      fetchAssessments();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, isGuest]);

  const fetchAssessments = async () => {
    try {
      const response = await assessmentApi.getUserAssessments();
      setAssessments(response.data.assessments);
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreBadgeClass = (category) => {
    switch (category) {
      case 'Strong Lending Candidate':
        return 'badge success';
      case 'Funding Ready':
        return 'badge info';
      case 'Preparation Required':
        return 'badge warning';
      default:
        return 'badge danger';
    }
  };

  if (!isAuthenticated || isGuest) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 font-heading mb-3">
              Sign in to view reports
            </h2>
            <p className="text-slate-600 mb-6">
              Create an account to save and access client assessment history.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/login">
                <Button variant="outline">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary">Create Account</Button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      
      <main className="flex-1 py-8 md:py-12" data-testid="reports-page">
        <div className="container-main">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">
                Client Reports
              </h1>
              <p className="text-slate-600">
                View and manage client funding readiness assessments
              </p>
            </div>
            <Link to="/assessment">
              <Button className="btn-primary" data-testid="new-assessment-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Assessment
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
            </div>
          ) : assessments.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-slate-300" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">
                No assessments yet
              </h2>
              <p className="text-slate-600 mb-6">
                Complete your first client funding readiness assessment to see it here.
              </p>
              <Link to="/assessment">
                <Button className="btn-cta">Start Assessment</Button>
              </Link>
            </div>
          ) : (
            <div className="grid gap-4">
              {assessments.map((assessment, index) => (
                <motion.div
                  key={assessment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/results/${assessment.id}`}>
                    <div className="card-interactive p-6 flex items-center gap-6" data-testid={`report-${assessment.id}`}>
                      <div className="w-14 h-14 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-sky-600 tabular-nums">
                          {assessment.total_score}
                        </span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 mb-1 truncate">
                          {assessment.business_name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(assessment.created_at).toLocaleDateString()}
                          </span>
                          <span className="capitalize truncate max-w-[200px]">
                            {(assessment.funding_purposes || [assessment.funding_purpose]).map(p => (p || '').replace(/_/g, ' ')).join(', ')}
                          </span>
                        </div>
                      </div>
                      
                      <span className={getScoreBadgeClass(assessment.score_category)}>
                        {assessment.score_category}
                      </span>
                      
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
